import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loader } from "@/base/components/Global";
import Icon from "@/base/components/Global/Icon";
import { getApolloLogoURLByLanguage } from "@/base/utils/iconUtil";
import { getPrimaryEmailValue, getQRCodeURL } from "@/base/utils/userUtil";
import {
  APOLLO_CARD_BG_PRIMITIVE,
  APOLLO_STAR_ICON,
} from "@/constants/blobIcons";
import { useAppSelector } from "@/store/hooks";
import { selectUserData } from "@/store/user";
import { createUseStyles } from "@/theme";

const useStyles = createUseStyles(({ spacing, borderRadius, card }) => ({
  loadingContainer: {
    backgroundColor: "#0D0D0C",
    borderRadius: borderRadius.l,
    aspectRatio: card.ratio,
    width: "100%",
    position: "relative",
    zIndex: 1,
    overflow: "hidden",
  },
  container: {
    backgroundColor: "#0D0D0C",
    backgroundImage: `url(${APOLLO_CARD_BG_PRIMITIVE})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    borderRadius: borderRadius.l,
    aspectRatio: card.ratio,
    width: "100%",
    zIndex: 1,
    position: "relative",
    overflow: "hidden",
  },
  cardContent: {
    display: "flex",
    position: "absolute",
    inset: spacing.xl,
    gap: spacing.xl,
    alignItems: "flex-end",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: [1, 1, "auto"],
    minWidth: 0,
    height: "100%",
  },
  brandBlock: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.xs,
  },
  brandWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.xs,
    alignItems: "flex-start",
  },
  starIcon: {
    marginLeft: spacing.xs,
  },
  logo: {
    width: 104,
    flexShrink: 0,
    alignSelf: "flex-start",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
  },
  name: {
    margin: 0,
    marginBottom: spacing.xxs,
    textTransform: "capitalize",
    color: "#FFFFFF",
    fontSize: 20,
    lineHeight: "24px",
    fontWeight: 600,
    letterSpacing: -0.6,
  },
  email: {
    margin: 0,
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 13,
    lineHeight: "18px",
    fontWeight: 450,
  },
  qrWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    padding: spacing.xs,
    width: 104,
    height: 104,
    flexShrink: 0,
  },
  qr: {
    width: 96,
    height: 96,
    display: "block",
  },
}));

type Props = {
  onNavigate?: () => void;
};

const preloadImageAsset = (source: string): Promise<void> =>
  new Promise((resolve) => {
    const image = new Image();
    image.addEventListener("load", () => resolve());
    image.addEventListener("error", () => resolve());
    image.src = source;

    if (image.complete) {
      resolve();
    }
  });

const MyCard: React.FC<Props> = () => {
  const classes = useStyles();
  const userData = useAppSelector(selectUserData);
  const { i18n } = useTranslation();
  const [isCardAssetsLoaded, setIsCardAssetsLoaded] = useState(false);

  const logoURL = useMemo(
    () => getApolloLogoURLByLanguage(i18n.language),
    [i18n.language],
  );
  const userGuid = userData.status === "succeeded" ? userData.guid : "";
  const qrCodeURL = userGuid ? getQRCodeURL(550, userGuid) : "";

  useEffect(() => {
    if (userData.status !== "succeeded") return;

    let isCancelled = false;

    setIsCardAssetsLoaded(false);

    void Promise.all(
      [APOLLO_CARD_BG_PRIMITIVE, APOLLO_STAR_ICON, logoURL, qrCodeURL].map(
        (source) => preloadImageAsset(source),
      ),
    ).then(() => {
      if (!isCancelled) {
        setIsCardAssetsLoaded(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [logoURL, qrCodeURL, userData.status]);

  if (userData.status !== "succeeded") return null;
  if (!isCardAssetsLoaded) {
    return (
      <div className={classes.loadingContainer}>
        <Loader showLoader isMinified />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.cardContent}>
        <div className={classes.leftColumn}>
          <div className={classes.brandWrapper}>
            <div className={classes.brandBlock}>
              <Icon
                icon={APOLLO_STAR_ICON}
                width={34}
                height={34}
                className={classes.starIcon}
              />
              <Icon icon={logoURL} className={classes.logo} width={110} />
            </div>
          </div>

          <div className={classes.userInfo}>
            <p className={classes.name}>
              {userData?.firstName.split(" ").slice(0, 2).join(" ") +
                " " +
                userData?.lastName}
            </p>
            <p className={classes.email}>{getPrimaryEmailValue(userData)}</p>
          </div>
        </div>

        <div className={classes.qrWrapper}>
          <img
            src={qrCodeURL}
            className={classes.qr}
            alt=""
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default MyCard;
